import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { subscriptionsApi, type VoucherValidationResponse, type PrimaryAdminData } from '@/api/endpoints/subscriptions'
import { useUI } from '@/stores/ui'
import { SUBSCRIPTION_CONFIG } from '@/config/subscription'
import AppFooter from '@/components/common/AppFooter'

const ROLE_OPTIONS = ['Priest', 'Deacon', 'Pastor', 'Parish Staff', 'Parish Volunteer', 'Other'] as const

export default function SubscriptionPage() {
  const addToast = useUI((state) => state.addToast)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [title, setTitle] = useState('')
  const [role, setRole] = useState<string>('')
  const [cellPhone, setCellPhone] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [voucher, setVoucher] = useState('')
  const [tosAccepted, setTosAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [validatingVoucher, setValidatingVoucher] = useState(false)
  const [voucherInfo, setVoucherInfo] = useState<VoucherValidationResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  // Auto-set title when role changes
  useEffect(() => {
    if (role === 'Priest' && title !== 'Father') {
      setTitle('Father')
    } else if (role === 'Deacon' && title !== 'Deacon') {
      setTitle('Deacon')
    }
  }, [role, title])

  // Validate voucher when it changes
  useEffect(() => {
    if (!voucher.trim()) {
      setVoucherInfo(null)
      return
    }

    const timeoutId = setTimeout(async () => {
      setValidatingVoucher(true)
      try {
        const result = await subscriptionsApi.validateVoucher(voucher.trim())
        setVoucherInfo(result)
        if (result.valid) {
          addToast({
            message: `Voucher valid! ${result.discount_percentage ? `${result.discount_percentage}% discount` : 'Discount applied'}`,
            type: 'success',
          })
        } else {
          addToast({
            message: 'Invalid voucher code',
            type: 'error',
          })
        }
      } catch (error) {
        setVoucherInfo(null)
      } finally {
        setValidatingVoucher(false)
      }
    }, 500) // Debounce validation

    return () => clearTimeout(timeoutId)
  }, [voucher, addToast])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    // Validate required fields
    if (!email.trim() || !password.trim() || !firstName.trim() || !lastName.trim()) {
      setError('Please fill in all required fields')
      return
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    
    if (!tosAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy to continue')
      return
    }
    
    try {
      setLoading(true)
      const adminData: PrimaryAdminData = {
        email: email.trim(),
        password: password.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        title: title.trim() || undefined,
        role_description: role || undefined,
        cell_phone: cellPhone.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zip_code: zipCode.trim() || undefined,
      }
      
      const res = await subscriptionsApi.createCheckout({
        admin_data: adminData,
        voucher_code: voucher.trim() || undefined,
        tos_accepted: tosAccepted,
      })
      
      if (res?.url) {
        window.location.href = res.url
      } else {
        setError('Failed to start checkout')
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to start checkout')
      addToast({
        message: e.response?.data?.detail || 'Failed to start checkout',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 page-transition">
      <div className="max-w-4xl mx-auto">
        <motion.div className="bg-white rounded-xl shadow-xl w-full p-6 sm:p-8 hover-raise"
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        >
          <div className="mb-4">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to Login
            </Link>
          </div>
          <h1 className="text-3xl font-bold mb-2">
            Subscribe to <span className="font-display">Catechism Cloud</span>
          </h1>
          
          {/* Marketing Copy */}
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-6 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Streamline Your Catechism Program</h2>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                Catechism Cloud is a comprehensive platform designed specifically for Catholic parishes to manage their religious education programs with ease and efficiency.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Complete Class Management:</strong> Create and manage classes, schedules, events, and calendars</li>
                <li><strong>Easy Parent Registration:</strong> Streamlined registration with QR codes and shareable links</li>
                <li><strong>Teacher & Admin Tools:</strong> Powerful dashboard for managing students and sending announcements</li>
                <li><strong>Secure Messaging:</strong> Built-in messaging system for teachers, parents, and administrators</li>
                <li><strong>Auto-Registration:</strong> Automatically assign students to classes based on age</li>
                <li><strong>Attendance & Tracking:</strong> Keep track of student participation and emergency contacts</li>
              </ul>
              <p className="mt-3 font-medium text-gray-900">
                Everything you need to run a successful catechism program, all in one place.
              </p>
            </div>
          </motion.div>
          
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="text-red-600 font-medium">Note:</span> This page is for Parish Registration. If you are a Parent, please <Link to="/register" className="text-yellow-600 hover:text-yellow-700 underline">register here</Link> instead. Also, consider sponsoring Catechism Cloud's annual subscription for your Parish!
            </p>
          </div>
          
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-gray-200 p-3 hover-raise">
              <div className="text-sm text-gray-600">Price</div>
              <div className="text-xl font-semibold">{SUBSCRIPTION_CONFIG.ANNUAL_PRICE_FORMATTED}</div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 hover-raise">
              <div className="text-sm text-gray-600">Billing</div>
              <div className="text-xl font-semibold">Annual</div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 hover-raise">
              <div className="text-sm text-gray-600">Processor</div>
              <div className="text-xl font-semibold flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Stripe ACH
              </div>
            </div>
          </div>

          <h2 className="text-xl font-semibold mb-4 mt-6">Primary Administrator Account Setup</h2>
          <p className="text-sm text-gray-600 mb-4">Create your Primary Administrator account. After payment, you'll be able to login immediately.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input 
                  type="email" 
                  className="input-field w-full focus-ring" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  placeholder="your.email@example.com"
                />
                <div className="text-xs text-gray-500 mt-1">This will be your login email</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Password *</label>
                <div className="relative">
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    className="input-field w-full pr-10 focus-ring" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    required 
                    minLength={8}
                    placeholder="Min 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input 
                  type="text" 
                  className="input-field w-full focus-ring" 
                  value={firstName} 
                  onChange={(e) => setFirstName(e.target.value)} 
                  required 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <input 
                  type="text" 
                  className="input-field w-full focus-ring" 
                  value={lastName} 
                  onChange={(e) => setLastName(e.target.value)} 
                  required 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Role (Optional)</label>
                <select 
                  className="input-field w-full focus-ring" 
                  value={role} 
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="">Select role...</option>
                  {ROLE_OPTIONS.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Title (Optional)</label>
                <input 
                  type="text" 
                  className="input-field w-full focus-ring" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  placeholder="Father, Mrs., etc."
                  disabled={(role === 'Priest' || role === 'Deacon')}
                />
                {role === 'Priest' && (
                  <div className="text-xs text-gray-500 mt-1">Auto-set to "Father" for Priests</div>
                )}
                {role === 'Deacon' && (
                  <div className="text-xs text-gray-500 mt-1">Auto-set to "Deacon"</div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cell Phone</label>
              <input 
                type="tel" 
                className="input-field w-full focus-ring" 
                value={cellPhone} 
                onChange={(e) => setCellPhone(e.target.value)} 
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <input 
                type="text" 
                className="input-field w-full focus-ring" 
                value={address} 
                onChange={(e) => setAddress(e.target.value)} 
                placeholder="Street address"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input 
                  type="text" 
                  className="input-field w-full focus-ring" 
                  value={city} 
                  onChange={(e) => setCity(e.target.value)} 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input 
                  type="text" 
                  className="input-field w-full focus-ring" 
                  value={state} 
                  onChange={(e) => setState(e.target.value)} 
                  placeholder="XX"
                  maxLength={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">ZIP Code</label>
                <input 
                  type="text" 
                  className="input-field w-full focus-ring" 
                  value={zipCode} 
                  onChange={(e) => setZipCode(e.target.value)} 
                />
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium mb-1">Voucher Code (optional)</label>
              <div className="relative">
                <input
                  className="input-field w-full focus-ring"
                  value={voucher}
                  onChange={(e) => setVoucher(e.target.value)}
                  placeholder="Enter voucher code"
                />
                {validatingVoucher && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">Validating...</div>
                )}
              </div>
              {voucherInfo?.valid && (
                <div className="mt-1 text-sm text-green-600">
                  ✓ Voucher valid! {voucherInfo.discount_percentage ? `${voucherInfo.discount_percentage}% discount` : 'Discount applied'}
                </div>
              )}
              {voucher && voucherInfo && !voucherInfo.valid && (
                <div className="mt-1 text-sm text-red-600">Invalid voucher code</div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-start gap-2">
                <input
                  id="tos"
                  type="checkbox"
                  className="mt-1 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  required
                />
                <label htmlFor="tos" className="text-sm text-gray-700">
                  I have read the <Link to="/privacy" className="text-yellow-600 hover:text-yellow-700 underline" target="_blank">Terms of Service, Data Usage Policy, and Privacy Policy</Link> and agree to them. *
                </label>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end pt-4">
              <button 
                className="btn-primary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60" 
                disabled={loading}
              >
                {loading ? 'Creating Account & Redirecting…' : 'Create Account & Proceed to Checkout'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
      <AppFooter />
    </div>
  )
}


